import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { apiClient } from '@/lib/api-client';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Phone, Lock, User, Loader2 } from 'lucide-react';

function apiErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object') {
    if ('errors' in error && error.errors && typeof error.errors === 'object') {
      const first = Object.values(error.errors as Record<string, string[]>)[0];
      if (first?.[0]) return first[0];
    }
    if ('message' in error && error.message) return String(error.message);
  }
  return fallback;
}

export default function SettingsTab() {
  const { t } = useTranslation();
  const { data: merchantResp, isLoading } = useQuery({ queryKey: ['/merchant'] });
  const merchant = merchantResp?.data;
  const { user, refreshUser } = useAuth();

  const [phone, setPhone] = useState('');
  const [phonePassword, setPhonePassword] = useState('');
  const phoneMutation = useMutation({
    mutationFn: async () => {
      const { error } = await apiClient.PATCH('/me/phone', { body: { phone, password: phonePassword } });
      if (error) throw error;
    },
    onSuccess: async () => {
      toast.success(t('cust_set.phone_updated'));
      setPhone('');
      setPhonePassword('');
      await refreshUser();
    },
    onError: (err) => toast.error(apiErrorMessage(err, t('cust_set.phone_update_error'))),
  });

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('');
  const passwordMutation = useMutation({
    mutationFn: async () => {
      const { error } = await apiClient.PATCH('/me/password', {
        body: { current_password: currentPassword, password: newPassword, password_confirmation: newPasswordConfirmation },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t('cust_set.password_updated'));
      setCurrentPassword('');
      setNewPassword('');
      setNewPasswordConfirmation('');
    },
    onError: (err) => toast.error(apiErrorMessage(err, t('cust_set.password_update_error'))),
  });

  if (isLoading) {
    return <div className="py-6 text-center text-muted-foreground">{t('cust_common.loading')}</div>;
  }

  return (
    <div className="space-y-6">
      {/* User profile info */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <User size={16} className="text-primary" />
          <h2 className="text-sm font-semibold text-foreground">{t('cust_set.personal_info')}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_set.name')}</label>
            <Input defaultValue={user?.full_name || ''} disabled className="bg-muted/50" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_set.current_phone')}</label>
            <Input defaultValue={user?.phone || ''} disabled className="bg-muted/50" />
          </div>
        </div>
      </div>

      {/* Phone change — self-service, requires current password (FR-82) */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Phone size={16} className="text-primary" />
          <h2 className="text-sm font-semibold text-foreground">{t('cust_set.change_phone')}</h2>
        </div>
        <form
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
          onSubmit={(e) => { e.preventDefault(); phoneMutation.mutate(); }}
        >
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_set.new_phone')}</label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+998901234567" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_set.current_password')}</label>
            <Input type="password" value={phonePassword} onChange={(e) => setPhonePassword(e.target.value)} placeholder={t('cust_set.current_password')} />
          </div>
          <div className="md:col-span-2">
            <Button type="submit" disabled={phoneMutation.isPending || !phone || !phonePassword}>
              {phoneMutation.isPending && <Loader2 size={16} className="animate-spin" />}
              {t('cust_set.update')}
            </Button>
          </div>
        </form>
      </div>

      {/* Password change — self-service, requires current password (FR-82) */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Lock size={16} className="text-primary" />
          <h2 className="text-sm font-semibold text-foreground">{t('cust_set.change_password')}</h2>
        </div>
        <form
          className="grid grid-cols-1 md:grid-cols-3 gap-4"
          onSubmit={(e) => { e.preventDefault(); passwordMutation.mutate(); }}
        >
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_set.current_password')}</label>
            <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_set.new_password')}</label>
            <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder={t('cust_set.min_8')} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('cust_set.confirm_password')}</label>
            <Input type="password" value={newPasswordConfirmation} onChange={(e) => setNewPasswordConfirmation(e.target.value)} />
          </div>
          <div className="md:col-span-3">
            <Button
              type="submit"
              disabled={passwordMutation.isPending || !currentPassword || !newPassword || !newPasswordConfirmation}
            >
              {passwordMutation.isPending && <Loader2 size={16} className="animate-spin" />}
              {t('cust_set.update_password')}
            </Button>
          </div>
        </form>
      </div>

      <div className="bg-card border border-border rounded-xl p-5">
        <h2 className="text-sm font-semibold text-foreground mb-4">{t('cust_set.receipt_template')}</h2>
        <Textarea
          rows={6}
          defaultValue={`===== ${merchant?.name || '{{merchant_name}}'} =====\n{{branch_address}}\n${t('cust_set.receipt_tel')}: {{branch_phone}}\n--------------------------\n{{items}}\n--------------------------\n${t('cust_set.receipt_total')}: {{total}}\n${t('cust_set.receipt_payment')}: {{payment_method}}\n\n${t('cust_set.receipt_thanks')}`}
          className="font-mono text-xs"
          disabled
        />
        <p className="text-xs text-muted-foreground mt-2">{t('cust_set.receipt_unavailable')}</p>
      </div>
    </div>
  );
}
